import { useMutation } from '@tanstack/react-query';
import { Dropdown, Form, MenuProps, Space, TableProps } from 'antd';
import dayjs from 'dayjs';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import axiosClient from 'apis/setup/axiosClient';
import { QUERY_UPDATE_LAT_LNG_CUSTOMER } from 'apis/GraphQL/Queries/customerQuery';
import { VARIABLE_UPDATE_LAT_LNG_CUSTOMER } from 'apis/GraphQL/Variables/customer';
import { EditIcon } from 'assets/svg';
import ButtonAction from 'components/Button/ButtonAction';
import TableComponent from 'components/Table/TableComponent';
import useQueryParam from 'hooks/useQueryPrams';
import { IParamQraphQL } from 'interfaces/GraphQL/GraphQL';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { toastError, toastSuccess } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { HocChangePagination } from 'utils/HocChangePagination';

const { MoreOutlined } = icons;

interface IProps extends React.PropsWithChildren<TableProps> {
  data: any;
  edit: number;
  onEdit: (data: any) => void;
  // setData: (data: any) => void;
  setEdit: (data: any) => void;
  refetch: (toFirstPage?: boolean, type?: any, headerRow?: any) => void;
  onViewForm?: (record: any) => void;
}

const TableCustomerPage = forwardRef((props: IProps, ref: any) => {
  const [form] = Form.useForm();
  const childRef = useRef<any>(null);

  const { data, onEdit, refetch, onViewForm } = props;

  const [open, setOpen] = useState(false);
  const [center, setCenter] = useState<any>();

  const queryParam = useQueryParam();
  const page = parseInt(queryParam.get('page') + '') || 1;
  const page_size =
    parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const mutationUpdateLocation = useMutation({
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      // console.log(res.data);
      res.data.updateCustomer;
      setOpen(false);
      setCenter(null);
      refetch();
      toastSuccess('Cập nhật vị trí thành công');
    },
    onError: (error) => {
      console.log(error);
      toastError('Cập nhật vị trí thất bại');
    },
  });

  const renderValueTable = (value: any) => value || '-';

  const handleExportPrint = async (record: any) => {
    try {
      const importId = record.typeSap == 1 ? 'IMP_KYC_CN' : 'IMP_KYC_DN';
      const sapCustomerId = record.sapCustomerId || record.id;

      const response = await axiosClient.get(
        `/customer-kyc/export-pdf/${importId}/${sapCustomerId}`,
        {
          responseType: 'blob',
        },
      );

      const blob = new Blob([response as any], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `KYC_Form_${sapCustomerId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      toastSuccess('Xuất form in thành công');
    } catch (error) {
      console.error(error);
      toastError('Có lỗi xảy ra khi xuất form in');
    }
  };

  const handleExportImage = async (record: any) => {
    try {
      const customerId = record.id;
      const response = await axiosClient.get(
        `/file/export-customer-images/${customerId}`,
        {
          responseType: 'blob',
        },
      );

      const blob = new Blob([response as any], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Customer_Images_${customerId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      toastSuccess('Xuất hình ảnh thành công');
    } catch (error: any) {
      console.error(error);
      if (error?.response?.status === 404) {
        toastError('Khách hàng không có hình ảnh');
      } else {
        toastError('Có lỗi xảy ra khi xuất hình ảnh');
      }
    }
  };

  const getActionItems = (record: any): MenuProps['items'] => [
    {
      key: 'view-form',
      label: 'Xem form',
      onClick: () => {
        if (onViewForm) onViewForm(record);
      },
    },
    {
      key: 'export-print',
      label: 'Xuất form in',
      onClick: () => handleExportPrint(record),
    },
    {
      key: 'export-image',
      label: 'Xuất hình ảnh',
      onClick: () => handleExportImage(record),
    },
  ];

  // plant_type = 1: Chi nhánh chỉ xem được data của chi nhánh mình
  // plant_type=0: Hội sở xem được data các chi nhánh mình quản lý
  const columns: ColumnsTypeCustom = [
    {
      title: 'STT',
      dataIndex: 'stt',
      width: 100,
      render: (_: any, __: any, index: number) =>
        (page - 1) * page_size + index + 1,
    },
    {
      title: 'Mã khách hàng',
      // dataIndex: ['customer', 'sapCustomerId'],
      dataIndex: 'sapCustomerId',
      width: 135,
    },
    {
      title: 'Mã DMS',
      // dataIndex: ['customer', 'id'],
      dataIndex: 'id',
      width: 145,
      render: renderValueTable,
    },
    {
      title: 'Mã cha',
      // dataIndex: 'mainCustomerId',
      // dataIndex: ['customer', 'mainCustomerId'],
      dataIndex: ['mainCustomer', 'sapCustomerId'],
      width: 145,
      render: renderValueTable,
    },
    // {
    //   title: 'Mã SAP',
    //   dataIndex: 'sapCustomerId',
    //   width: 150,
    //   render: renderValueTable,
    // },
    {
      title: 'Tên khách hàng',
      // dataIndex: ['customer', 'storeName'],
      dataIndex: 'storeName',
      width: 220,
      // render: (_: any, record: any) => {
      //   const firstName = record?.firstName || '';
      //   const storeName = record?.storeName || '';

      //   return record.typeSap == 1
      //     ? [firstName, storeName].filter(Boolean)?.join(' ')
      //     : record?.storeName;
      // },
    },
    {
      title: 'Loại khách hàng',
      // dataIndex: ['customer', 'typeSap'],
      dataIndex: 'typeSap',
      width: 200,
      render: (value: any) => {
        if (value !== null && value !== undefined) {
          return value == 1 ? 'Cá nhân' : 'Doanh nghiệp';
        } else {
          return '-';
        }
      },
    },
    {
      title: 'Mã số thuế',
      // dataIndex: ['customer', 'taxId'],
      dataIndex: 'taxId',
      width: 160,
      render: renderValueTable,
    },
    {
      title: 'SĐT',
      // dataIndex: ['customer', 'phone'],
      dataIndex: 'phone',
      width: 150,
      render: renderValueTable,
    },
    {
      title: 'CCCD',
      // dataIndex: ['customer', 'cccd'],
      dataIndex: 'cccd',
      width: 160,
      render: renderValueTable,
    },
    {
      title: 'Passport',
      // dataIndex: ['customer', 'cccd'],
      dataIndex: 'passportId',
      width: 160,
      render: renderValueTable,
    },
    {
      title: 'Email',
      // dataIndex: ['customer', 'email'],
      dataIndex: 'email',
      width: 185,
      render: renderValueTable,
    },
    {
      title: 'Danh xưng',
      // dataIndex: ['customer', 'titleSap'],
      dataIndex: 'titleSap',
      width: 200,
      render: (value: any) => {
        if (value !== null && value !== undefined) {
          return value == '0001' ? 'Nữ' : value == '0002' ? 'Nam' : 'Công ty';
        } else {
          return '-';
        }
      },
    },
    // {
    //   title: 'Họ và tên KH',
    //   // dataIndex: ['customer', 'name'],
    //   width: 220,
    //   render: () => '-',
    // },
    {
      title: 'Tên người liên hệ',
      // dataIndex: ['customer', 'name'],
      dataIndex: 'name',
      width: 180,
      render: renderValueTable,
    },

    {
      title: 'Giới tính',
      // dataIndex: ['customer', 'gender'],
      dataIndex: 'gender',
      width: 100,
      render: renderValueTable,
    },
    {
      title: 'Sinh nhật',
      // dataIndex: ['customer', 'dayofbirth'],
      dataIndex: 'dayofbirth',
      width: 150,
      render: (text: any) => (text && dayjs(text).format('DD/MM/YYYY')) || '-',
    },
    {
      title: 'Hạng thẻ',
      dataIndex: ['trank', 'name'],
      width: 100,
      render: renderValueTable,
    },

    // {
    //   title: 'Loại VIP Hàng Công',
    //   dataIndex: ['trankProduct', 'name'],
    //   width: 160,
    //   render: renderValueTable,
    // },
    // {
    //   title: 'Loại VIP Hàng Món',
    //   dataIndex: ['trankProcess', 'name'],
    //   width: 160,
    //   render: renderValueTable,
    // },

    // {
    //   title: 'Doanh thu',
    //   dataIndex: 'doanhthu',
    //   width: 150,
    //   align: 'right',
    //   render: (value: any) => (value ? formatCurrency(value) : '-'),
    // },
    {
      title: 'Địa chỉ',
      // dataIndex: ['customer', 'address'],
      dataIndex: 'address',
      width: 210,
      render: renderValueTable,
    },
    {
      title: 'Tỉnh/Thành Phố',
      // dataIndex: ['customer', 'tcity', 'name'],
      dataIndex: ['tcity', 'name'],
      width: 150,
      render: renderValueTable,
    },
    {
      title: 'Quận/ Huyện',
      // dataIndex: ['customer', 'tdistrict', 'name'],
      dataIndex: ['tdistrict', 'name'],
      width: 170,
      render: renderValueTable,
    },
    {
      title: 'Phường/Xã',
      // dataIndex: ['customer', 'tward', 'name'],
      dataIndex: ['tward', 'name'],
      width: 180,
      render: renderValueTable,
    },
    {
      title: 'Tỉnh/Thành phố mới',
      dataIndex: ['tcityNew', 'name'],
      width: 170,
      render: renderValueTable,
    },
    {
      title: 'Phường/Xã mới',
      dataIndex: ['twardNew', 'name'],
      width: 170,
      render: renderValueTable,
    },

    {
      title: 'Tọa độ X',
      // dataIndex: ['customer', 'latitude'],
      dataIndex: 'latitude',
      width: 154,
      render: (value: any) => (value ? parseFloat(value)?.toFixed(12) : '-'),
    },
    {
      title: 'Tọa độ Y',
      // dataIndex: ['customer', 'longitude'],
      dataIndex: 'longitude',
      width: 154,
      render: (value: any) => (value ? parseFloat(value)?.toFixed(12) : '-'),
    },
    {
      title: 'Trạng thái',
      // dataIndex: ['customer', 'status'],
      dataIndex: 'status',
      width: 120,
      render: (value: any) => {
        if (value !== null && value !== undefined) {
          return value == 'A100_01' ? 'Hoạt động' : 'Không hoạt động';
        } else {
          return '-';
        }
      },
    },

    {
      title: 'Người tạo',
      // dataIndex: ['customer', 'tcreateUser', 'fullName'],
      dataIndex: ['tcreateUser', 'fullName'],
      className: 'text-left ',
      width: 141,
    },
    {
      title: 'Ngày tạo',
      // dataIndex: ['customer', 'createDate'],
      dataIndex: 'createDate',
      type: 'date',
      className: '',
      width: 170,
      render: (value: any) =>
        value && dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Người cập nhật',
      // dataIndex: ['customer', 'tmodifyUser', 'fullName'],
      dataIndex: ['tmodifyUser', 'fullName'],
      className: 'text-left ',
      width: 141,
    },
    {
      title: 'Ngày cập nhật',
      // dataIndex: ['customer', 'modifyDate'],
      dataIndex: 'modifyDate',
      className: '',
      type: 'date',
      width: 170,
      render: (value: any) =>
        value && dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      width: 100,
      fixed: 'right',
      align: 'center',
      render: (__, record: any) => (
        <Space size={'middle'} className="text-lg ">
          {/* <ButtonAction tooltip="Xem chi tiết">
          <EyeIcon />
        </ButtonAction> */}
          <ButtonAction
            tooltip="Chỉnh sửa"
            onClick={() => handleEditRow(record)}
          >
            <EditIcon />
          </ButtonAction>
          <Dropdown
            menu={{ items: getActionItems(record) }}
            trigger={['click']}
            placement="bottomRight"
          >
            <ButtonAction>
              <MoreOutlined
                style={{
                  fontSize: '20px',
                  color: '#00538F',
                  fontWeight: 'bold',
                }}
              />
            </ButtonAction>
          </Dropdown>
        </Space>
      ),
    },
  ];

  useImperativeHandle(ref, () => ({
    // handleeAddRow,
    handleExportExcel,
  }));

  function handleExportExcel() {
    // exportExcel_v2(columns, data.items);
    refetch(true, 'export', columns);
  }

  // function handleeAddRow(e: any) {
  //   setEdit(0);
  //   form.resetFields();
  // }

  function handleEditRow(record: any) {
    // setCenter(record);
    // setOpen(true);

    onEdit(record);
  }

  const updateLocation = (point: any) => {
    if (point === null || point === undefined) {
      toastError('Chưa chọn vị trí để cập nhật');
      return;
    }
    mutationUpdateLocation.mutate({
      query: QUERY_UPDATE_LAT_LNG_CUSTOMER,
      variables: VARIABLE_UPDATE_LAT_LNG_CUSTOMER(center?.id, {
        lat: point?.lat?.toString(),
        lng: point?.lng?.toString(),
      }),
    });
  };

  const handleOK = () => {
    if (childRef.current) {
      childRef.current.handleOK();
    }
  };

  return (
    <Content>
      <Form form={form}>
        <TableComponent
          columns={columns}
          dataSource={data.items}
          loading={props.loading}
          pagination={{
            current: data?.pageInfo?.currentPage,
            pageSize: data?.pageInfo?.itemsPerPage,
            total: data.pageInfo?.totalItems,
            onChange: HocChangePagination(),
          }}
        />
      </Form>
      {/* <ModalMapLocation
        open={open}
        title={'Cập nhật vị trí'}
        setOpen={setOpen}
        center={{
          // ...center,
          lat: parseFloat(center?.latitude) || null,
          lng: parseFloat(center?.longitude) || null,
        }}
        updateLocation={updateLocation}
      /> */}

      {/* <Modal
        open={open}
        okText="Lưu"
        cancelText="Hủy"
        onCancel={() => setOpen(false)}
        onOk={handleOK}
        closable={false}
        width={'max-content'}
      >
        <MapLocation
          center={{
            ...center,
            lat: parseFloat(center?.latitude) || null,
            lng: parseFloat(center?.longitude) || null,
          }}
          scale={0.8}
          className="w-[900px] h-[600px]"
          refetch={refetch}
          // handleOK={handleOK}
          updateLocation={updateLocation}
          ref={childRef}
        />
      </Modal> */}
    </Content>
  );
});

export default TableCustomerPage;
